/*
 * EB Sticky CTA — pill flotante en móvil para PDP con eb-product-hero.
 * - Detecta cuándo el .eb-hero__cta sale del viewport (scroll hacia abajo) y muestra el pill.
 * - Click en pill: dispara form.requestSubmit(realBtn) o AJAX add (si está activo el upgrade).
 * - Reaparece la versión completa cuando el botón principal vuelve a verse.
 */
(function () {
  const MOBILE_QUERY = '(max-width: 849.98px)';

  class EBStickyCTA extends HTMLElement {
    constructor() {
      super();
      this.heroCta = null;
      this.heroForm = null;
      this.observer = null;
      this.isStuck = false;
      this.mql = window.matchMedia(MOBILE_QUERY);
      this._onMqlChange = () => this.setup();
      this._onClick = (e) => this.handleClick(e);
    }

    connectedCallback() {
      this.setup();
      this.mql.addEventListener('change', this._onMqlChange);
      const btn = this.querySelector('[data-eb-sticky-action]');
      if (btn) btn.addEventListener('click', this._onClick);
      // Re-bind after dynamic variant updates that could re-render the hero
      document.addEventListener('variant:update', this._rebindHero);
    }

    disconnectedCallback() {
      this.cleanupObserver();
      this.mql.removeEventListener('change', this._onMqlChange);
      const btn = this.querySelector('[data-eb-sticky-action]');
      if (btn) btn.removeEventListener('click', this._onClick);
      document.removeEventListener('variant:update', this._rebindHero);
    }

    _rebindHero = () => {
      // The hero may re-render its form on variant change — re-find references.
      this.heroCta = document.querySelector('.eb-hero__cta');
      this.heroForm = document.querySelector('.eb-hero__form');
    };

    setup() {
      this.cleanupObserver();

      if (!this.mql.matches) {
        this.hide(true);
        this.removeAttribute('data-active');
        return;
      }

      this.heroCta = document.querySelector('.eb-hero__cta');
      this.heroForm = document.querySelector('.eb-hero__form');
      if (!this.heroCta || !this.heroForm) {
        this.removeAttribute('data-active');
        return;
      }

      this.setAttribute('data-active', '');

      // Sync price label if hero exposes one
      this._syncPrice();

      this.observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          this.hide();
          return;
        }
        const rect = entry.target.getBoundingClientRect();
        // Only stick when the main CTA scrolled ABOVE the viewport (user moved past it).
        if (rect.bottom < 0) this.show();
      }, { threshold: 0 });

      this.observer.observe(this.heroCta);
    }

    cleanupObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    _syncPrice() {
      const heroPrice = this.heroCta?.querySelector('[data-eb-price]');
      const stickyPrice = this.querySelector('[data-eb-sticky-price]');
      if (heroPrice && stickyPrice) {
        stickyPrice.textContent = (heroPrice.textContent || '').replace(/^·\s*/, '').trim();
      }
    }

    show() {
      if (this.isStuck) return;
      this.isStuck = true;
      this._syncPrice();
      this.setAttribute('data-stuck', 'true');
    }

    hide(immediate) {
      if (!this.isStuck && !immediate) return;
      this.isStuck = false;
      this.setAttribute('data-stuck', 'false');
    }

    async handleClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.heroCta || !this.heroForm) return;
      if (this.heroCta.disabled) return;

      const pill = event.currentTarget;
      pill.classList.add('eb-sticky-cta__pill--loading');
      pill.disabled = true;

      try {
        await this._ajaxAdd();
        pill.classList.add('eb-sticky-cta__pill--success');
        setTimeout(() => pill.classList.remove('eb-sticky-cta__pill--success'), 700);
      } catch (e) {
        // Fallback: native submit (full page nav to /cart)
        if (this.heroForm.requestSubmit) this.heroForm.requestSubmit(this.heroCta);
        else this.heroForm.submit();
      } finally {
        pill.classList.remove('eb-sticky-cta__pill--loading');
        pill.disabled = false;
      }
    }

    async _ajaxAdd() {
      const fd = new FormData(this.heroForm);
      // Force quantity if missing
      if (!fd.get('quantity')) fd.set('quantity', '1');
      // Request section refresh so the cart drawer/header morph picks it up
      fd.append('sections', 'cart-drawer,header,header-group');
      fd.append('sections_url', window.location.pathname);

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.description || 'add failed');
      }
      const data = await res.json();

      // Refresh global cart state and notify theme
      const cart = await window.EBCart?.refresh('eb-sticky-cta');
      const detailResource = cart || { item_count: data.items_subtotal_price ? data.quantity : 1 };

      // Native cart drawer listens to 'cart:add' event name (CartAddEvent)
      document.dispatchEvent(new CustomEvent('cart:add', {
        detail: { resource: cart || data, data: { source: 'eb-sticky-cta', itemCount: cart?.item_count } }
      }));
      document.dispatchEvent(new CustomEvent('cart:update', {
        detail: { resource: cart || data, data: { source: 'eb-sticky-cta', itemCount: cart?.item_count } }
      }));

      // If the theme has a cart drawer, open it
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    }
  }

  if (!customElements.get('eb-sticky-cta')) {
    customElements.define('eb-sticky-cta', EBStickyCTA);
  }
})();
